import { z } from "zod";

const STREAM_ADMISSION_FIELD = "__cnsAdmissionState";
export type EntitySelectionAdmissionState = "admitted";

const ToolInvokeLooseSchema = z.object({
  requiresAction: z.boolean().optional(),
  toolCallId: z.string().optional(),
  actionType: z.string().optional(),
  description: z.string().optional(),
  priority: z.enum(["high", "medium", "low"]).optional(),
});

export const AssumptionItemLooseSchema = z.object({
  id: z.string().optional(),
  statement: z.string().optional(),
  evidence: z.number().optional(),
  importance: z.number().optional(),
});

export const ClarifyingQuestionItemLooseSchema = z.object({
  id: z.string().optional(),
  question: z.string().optional(),
});

const HypothesisThemeSchema = z.enum(["desirability", "feasibility", "viability"]);

export const HypothesisItemLooseSchema = z.object({
  id: z.string().optional(),
  statement: z.string().optional(),
  hypothesisTheme: HypothesisThemeSchema.optional(),
  sourceAssumptionIds: z.array(z.string()).optional(),
});

const ExperimentCardScoresLooseSchema = z.object({
  setupComplexity: z.number().optional(),
  runTime: z.number().optional(),
  cost: z.number().optional(),
  dataReliability: z.number().optional(),
});

export const ExperimentCardItemLooseSchema = z.object({
  id: z.string().optional(),
  linkedHypothesisId: z.string().optional(),
  title: z.string().optional(),
  statement: z.string().optional(),
  experimentType: z.string().optional(),
  description: z.string().optional(),
  successCriteria: z.string().optional(),
  hypothesisTheme: HypothesisThemeSchema.optional(),
  scores: ExperimentCardScoresLooseSchema.optional(),
  keyMetrics: z
    .array(
      z.object({
        id: z.string().optional(),
        name: z.string().optional(),
        target: z.string().optional(),
        notes: z.string().nullable().optional(),
      }),
    )
    .optional(),
});

const EntityCollectionLooseSchema = z.object({
  type: z.string().optional(),
  items: z.array(z.unknown()).optional(),
  toolInvoke: ToolInvokeLooseSchema.optional(),
  toolResult: z
    .object({
      summary: z.string().optional(),
      status: z.string().optional(),
    })
    .optional(),
});

export const EntitySelectionArgsSchema = z.object({
  entities: z.array(EntityCollectionLooseSchema).optional(),
  [STREAM_ADMISSION_FIELD]: z.literal("admitted").optional(),
});

export const AssumptionSelectionResolveSchema = z.object({
  toolCallId: z.string().min(1),
  selectedAssumptionIds: z.array(z.string().min(1)).min(1),
});

const AnsweredQuestionsSubmissionSchema = z.object({
  answeredQuestions: z
    .array(
      z.object({
        id: z.string().min(1),
        answer: z.string().min(1).max(4_000),
      }),
    )
    .min(1)
    .max(5),
  skipped: z.never().optional(),
});

const SkippedQuestionsSubmissionSchema = z.object({
  skipped: z.literal(true),
  answeredQuestions: z.never().optional(),
});

/** Bounded action-specific fields sent to the Action Request endpoint. */
export const QuestionAnswerSubmissionSchema = z.union([
  AnsweredQuestionsSubmissionSchema,
  SkippedQuestionsSubmissionSchema,
]);

/** assistant-ui transport identity plus the action-specific submission. */
export const QuestionAnswerResolveSchema = z.union([
  AnsweredQuestionsSubmissionSchema.extend({
    toolCallId: z.string().min(1),
  }),
  SkippedQuestionsSubmissionSchema.extend({
    toolCallId: z.string().min(1),
  }),
]);

export const HypothesisSelectionResolveSchema = z.object({
  toolCallId: z.string().min(1),
  selectedHypothesisId: z.string().min(1),
});

export const ExperimentCardSelectionResolveSchema = z.object({
  toolCallId: z.string().min(1),
  selectedExperimentCardId: z.string().min(1),
});

/** One, and only one, Experiment Design selection may cross the tool-result boundary. */
export const ExperimentDesignSelectionResolveSchema = z.union([
  HypothesisSelectionResolveSchema.extend({
    selectedExperimentCardId: z.never().optional(),
  }),
  ExperimentCardSelectionResolveSchema.extend({
    selectedHypothesisId: z.never().optional(),
  }),
]);

export type ExperimentDesignSelectionResult = z.infer<
  typeof ExperimentDesignSelectionResolveSchema
>;

export type AssumptionItemView = z.infer<typeof AssumptionItemLooseSchema>;
export type ClarifyingQuestionItemView = z.infer<typeof ClarifyingQuestionItemLooseSchema>;
export type HypothesisItemView = z.infer<typeof HypothesisItemLooseSchema>;
export type ExperimentCardItemView = z.infer<typeof ExperimentCardItemLooseSchema>;

export type ParsedAssumptionCollection = {
  kind: "assumption";
  toolCallId: string;
  actionType: "assumption_selection";
  description?: string;
  items: AssumptionItemView[];
};

export type ParsedClarifyingQuestionCollection = {
  kind: "clarifying_question";
  toolCallId: string;
  actionType: "question_answer";
  description?: string;
  items: ClarifyingQuestionItemView[];
};

export type ParsedHypothesisCollection = {
  kind: "hypothesis";
  toolCallId: string;
  actionType: "hypothesis_selection";
  description?: string;
  items: HypothesisItemView[];
};

export type ParsedExperimentCardCollection = {
  kind: "experiment_card";
  toolCallId: string;
  actionType: "experiment_card_selection";
  description?: string;
  items: ExperimentCardItemView[];
};

export type ParsedDiscoveryActionableCollection =
  | ParsedAssumptionCollection
  | ParsedClarifyingQuestionCollection;

export type ParsedExperimentDesignActionableCollection =
  | ParsedHypothesisCollection
  | ParsedExperimentCardCollection;

export type ParsedActionableCollection =
  | ParsedDiscoveryActionableCollection
  | ParsedExperimentDesignActionableCollection;

export type ParsedCompletedCollection = {
  kind: string;
  summary: string;
};

export type ParsedEntityCollectionArgs = {
  /** True when actionable collections exist or any entity has toolResult (streaming gate). */
  hasRenderable: boolean;
  hasActionable: boolean;
  actionable: ParsedActionableCollection[];
  completed: ParsedCompletedCollection[];
};

/**
 * Marks close-time canonical arguments as admitted while the workflow snapshot catches up.
 */
export function withCanonicalEntitySelectionAdmission(
  args: Record<string, unknown>,
): Record<string, unknown> {
  return { ...args, [STREAM_ADMISSION_FIELD]: "admitted" };
}

/**
 * Returns the transient stream admission state; persisted and reloaded cards have no marker.
 */
export function entitySelectionAdmissionState(
  args: unknown,
): EntitySelectionAdmissionState | undefined {
  if (args == null || typeof args !== "object") return undefined;
  const value = (args as Record<string, unknown>)[STREAM_ADMISSION_FIELD];
  return value === "admitted" ? value : undefined;
}

/**
 * Removes transient stream admission metadata after the terminal workflow snapshot is applied.
 */
export function withoutEntitySelectionAdmissionState(
  args: Record<string, unknown>,
): Record<string, unknown> {
  const next = { ...args };
  delete next[STREAM_ADMISSION_FIELD];
  return next;
}

/** Streaming gate: skeleton until toolInvoke or toolResult is parseable. */
export function hasRenderableEntityCollections(args: unknown): boolean {
  const root = EntitySelectionArgsSchema.safeParse(args);
  if (!root.success) {
    return false;
  }
  return (root.data.entities ?? []).some(
    (collection) =>
      collection.toolInvoke?.requiresAction === true || Boolean(collection.toolResult),
  );
}

function parseAssumptionItems(items: unknown[] | undefined): AssumptionItemView[] {
  return (items ?? [])
    .map((item) => AssumptionItemLooseSchema.safeParse(item))
    .filter((parsed) => parsed.success)
    .map((parsed) => parsed.data);
}

function parseQuestionItems(items: unknown[] | undefined): ClarifyingQuestionItemView[] {
  return (items ?? [])
    .map((item) => ClarifyingQuestionItemLooseSchema.safeParse(item))
    .filter((parsed) => parsed.success)
    .map((parsed) => parsed.data);
}

function parseHypothesisItems(items: unknown[] | undefined): HypothesisItemView[] {
  return (items ?? [])
    .map((item) => HypothesisItemLooseSchema.safeParse(item))
    .filter((parsed) => parsed.success)
    .map((parsed) => parsed.data);
}

function parseExperimentCardItems(items: unknown[] | undefined): ExperimentCardItemView[] {
  return (items ?? [])
    .map((item) => ExperimentCardItemLooseSchema.safeParse(item))
    .filter((parsed) => parsed.success)
    .map((parsed) => parsed.data);
}

/**
 * The Action Request contract owns the operation discriminator. Collection `type` is retained
 * only as historical presentation data and must not become a second workflow authority.
 */
function selectionKind(
  actionType: string | undefined,
): ParsedActionableCollection["kind"] | undefined {
  switch (actionType) {
    case "assumption_selection":
      return "assumption";
    case "question_answer":
      return "clarifying_question";
    case "hypothesis_selection":
      return "hypothesis";
    case "experiment_card_selection":
      return "experiment_card";
    default:
      return undefined;
  }
}

/**
 * Partial-tolerant parse for discovery entity collections (streaming-safe).
 */
export function parseEntityCollectionArgs(args: unknown): ParsedEntityCollectionArgs {
  const root = EntitySelectionArgsSchema.safeParse(args);
  const entities = root.success ? (root.data.entities ?? []) : [];

  const actionable: ParsedActionableCollection[] = [];
  const completed: ParsedCompletedCollection[] = [];

  for (const collection of entities) {
    const toolInvoke = collection.toolInvoke;
    if (toolInvoke?.requiresAction && toolInvoke.toolCallId) {
      const kind = selectionKind(toolInvoke.actionType);
      if (kind === "assumption") {
        actionable.push({
          kind: "assumption",
          toolCallId: toolInvoke.toolCallId,
          actionType: "assumption_selection",
          description: toolInvoke.description,
          items: parseAssumptionItems(collection.items as unknown[] | undefined),
        });
      } else if (kind === "clarifying_question") {
        actionable.push({
          kind: "clarifying_question",
          toolCallId: toolInvoke.toolCallId,
          actionType: "question_answer",
          description: toolInvoke.description,
          items: parseQuestionItems(collection.items as unknown[] | undefined),
        });
      } else if (kind === "hypothesis") {
        actionable.push({
          kind: "hypothesis",
          toolCallId: toolInvoke.toolCallId,
          actionType: "hypothesis_selection",
          description: toolInvoke.description,
          items: parseHypothesisItems(collection.items as unknown[] | undefined),
        });
      } else if (kind === "experiment_card") {
        actionable.push({
          kind: "experiment_card",
          toolCallId: toolInvoke.toolCallId,
          actionType: "experiment_card_selection",
          description: toolInvoke.description,
          items: parseExperimentCardItems(collection.items as unknown[] | undefined),
        });
      }
      continue;
    }

    if (collection.toolResult?.summary) {
      completed.push({
        kind:
          selectionKind(collection.toolInvoke?.actionType) ??
          collection.type ??
          "unknown",
        summary: collection.toolResult.summary,
      });
    }
  }

  const hasRenderable = entities.some(
    (collection) =>
      collection.toolInvoke?.requiresAction === true || Boolean(collection.toolResult),
  );

  return {
    hasRenderable,
    hasActionable: actionable.length > 0,
    actionable,
    completed,
  };
}
